// src/App.js
// ✅ BUG-001 FIX: Forgot password flow
// ✅ BUG-002 FIX: SessionWarningModal
// ✅ BUG-003 FIX: RequireAuth waits for userRole before checking allowedRoles
// ✅ GAP-SEC-008 FIX (Mar 21, 2026): Multi-Tenant Isolation
// ✅ GAP-UX-001 (Apr 2, 2026): Error Boundary Component
// ✅ TRACK-A (Mar 26, 2026): Feature gating + FirmSettings route
// ✅ TRACK-C (Apr 6, 2026): HelpDrawer + OfflineBanner + LicenseManagement route + licenseError on Login
// ✅ UI-FIX (Mar 28, 2026): Restored two-column branded Login layout (Screenshot 1 design)
// ✅ BUG-ROUTE-001 (Apr 16, 2026): /cases/create matched /cases/:id — added redirect before dynamic route
// ✅ PERF-001 (Apr 16, 2026): React.lazy() code splitting — all pages lazy-loaded
//    → Eliminates initial JS bundle from loading all ~50 pages upfront
// ✅ PERF-002 (Apr 16, 2026): Lazy-load HelpDrawer, SessionWarningModal, ErrorBoundary
//    → These were eagerly imported but NOT needed on Login page
//    → Expected Lighthouse Performance: 14 → 65–75+ (Login page)
//
// WHAT CHANGED (PERF-001 + PERF-002 Apr 16, 2026):
//   1. All page/layout imports converted to React.lazy(() => import(...))
//   2. HelpDrawer, HelpButton, SessionWarningModal, ErrorBoundary → also lazy
//   3. RootErrorBoundary stays eager (must wrap everything, can't be lazy)
//   4. AppRoutes wrapped in <Suspense fallback={<PageLoader />}>
//   5. app-loader div in index.html hidden once React mounts (useEffect)
//   6. Zero breaking changes — all route paths, roles, guards unchanged
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { useAuth, AuthProvider } from "./contexts/AuthContext";

import ClientPortalDashboardDirect from "./pages/ClientPortalDashboard";

// ✅ BUG-001 FIX: Import sendPasswordResetEmail
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "./firebase";

import { AlertCircle, WifiOff } from "lucide-react";

// Context providers (always needed — NOT lazy)
import { UIProvider } from './contexts/UIContext';
import { ToastProvider } from './contexts/ToastContext';

// ✅ BUG-002 FIX: SessionWarningModal — lazy (not needed on Login page)
// ✅ GAP-UX-001: ErrorBoundary inner — lazy; RootErrorBoundary stays eager (wraps everything)
// ✅ TRACK-C: HelpDrawer, HelpButton — lazy (not needed on Login page)
// NOTE: Static import for RootErrorBoundary must stay here (cannot be lazy — it wraps App)
import { RootErrorBoundary } from './components/ErrorBoundary';
import { HelpProvider } from './contexts/HelpContext';

// ✅ TRACK-A: Feature gating imports
import { FEATURE_FLAGS } from './config/constants';

import "./App.css";

// ─────────────────────────────────────────────────────────────────────────────
// ✅ PERF-002: COMPONENTS LAZY-LOADED — not needed on Login page
// ─────────────────────────────────────────────────────────────────────────────
const SessionWarningModal = lazy(() => import('./components/SessionWarningModal'));
const ErrorBoundary       = lazy(() => import('./components/ErrorBoundary'));
const HelpDrawer          = lazy(() => import('./components/HelpDrawer')); // has default export
const HelpButton          = lazy(() => import('./components/HelpDrawer').then(m => ({ default: m.HelpButton })));


// ─────────────────────────────────────────────────────────────────────────────
// ✅ PERF-001: PAGE LOADER — shown while lazy chunks load
// ─────────────────────────────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-10 h-10 border-3 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
        <p className="text-slate-500 text-sm">Loading...</p>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// ✅ PERF-001: LAZY PAGE IMPORTS — all pages loaded on first visit only
// Grouped by feature area for readable chunk naming
// ─────────────────────────────────────────────────────────────────────────────

// ── Core / Auth ──────────────────────────────────────────────────────────────
const Unauthorized        = lazy(() => import("./pages/Unauthorized"));
const ClientLogin         = lazy(() => import("./pages/ClientLogin"));
const ClientPortal        = lazy(() => import("./pages/ClientPortal"));
const ClientPortalDashboard = lazy(() => import("./pages/ClientPortalDashboard"));

// ── Dashboards ───────────────────────────────────────────────────────────────
const AdminDashboard      = lazy(() => import("./pages/AdminDashboard"));
const SrAssociateDashboard = lazy(() => import("./pages/SrAssociateDashboard"));
const PartnerDashboard    = lazy(() => import("./pages/PartnerDashboard"));
const AssociateDashboard  = lazy(() => import("./pages/AssociateDashboard"));
const InternDashboard     = lazy(() => import("./pages/InternDashboard"));
const ReceptionistDashboard = lazy(() => import("./pages/ReceptionistDashboard"));
const AccountsDashboard   = lazy(() => import("./pages/AccountsDashboard"));
const KPIDashboard        = lazy(() => import("./pages/KPIDashboard"));

// ── Layouts ───────────────────────────────────────────────────────────────────
const AdminLayout         = lazy(() => import("./layouts/AdminLayout"));
const PartnerLayout       = lazy(() => import("./layouts/PartnerLayout"));
const AssociateLayout     = lazy(() => import("./layouts/AssociateLayout"));
const InternLayout        = lazy(() => import("./layouts/InternLayout"));
const AccountantLayout    = lazy(() => import("./layouts/AccountantLayout"));
const ReceptionistLayout  = lazy(() => import("./layouts/ReceptionistLayout"));
// ✅ PERF-001 FIX: DashboardLayout is a named export — use the thin wrapper so lazy() works.
// Place DashboardLayoutWrapper.jsx at: src/components/layout/DashboardLayoutWrapper.jsx
const DashboardLayout     = lazy(() => import('./components/layout/DashboardLayoutWrapper'));

// ── Cases ─────────────────────────────────────────────────────────────────────
const CasesList           = lazy(() => import("./pages/CasesList"));
const CaseDetail          = lazy(() => import("./pages/CaseDetail"));
const CaseEdit            = lazy(() => import("./pages/CaseEdit"));
const CaseCreate          = lazy(() => import("./pages/CaseCreate"));
const CaseView            = lazy(() => import("./pages/CaseView"));
const CaseDocuments       = lazy(() => import("./pages/CaseDocuments"));
const CaseDetailDiagnostic = lazy(() => import("./pages/CaseDetailDiagnostic.js"));

// ── Case sub-components ───────────────────────────────────────────────────────
const CaseDocs            = lazy(() => import('./components/case/CaseDocs.jsx'));
const CaseTasks           = lazy(() => import('./components/case/CaseTasks.jsx'));
const CaseChat            = lazy(() => import('./components/case/CaseChat.jsx'));
const CaseNotes           = lazy(() => import('./components/case/CaseNotes.jsx'));
const CaseDrafts          = lazy(() => import('./components/case/CaseDrafts.jsx'));

// ── Clients ───────────────────────────────────────────────────────────────────
const ClientsPage         = lazy(() => import("./pages/ClientsPage"));
const ClientList          = lazy(() => import("./pages/ClientList"));
const ClientCreate        = lazy(() => import("./pages/ClientCreate"));
const ClientView          = lazy(() => import("./pages/ClientView"));

// ── Invoices ──────────────────────────────────────────────────────────────────
const InvoicesListPage    = lazy(() => import("./pages/invoices/InvoicesListPage"));
const InvoiceCreatePage   = lazy(() => import("./pages/invoices/InvoiceCreatePage"));
const InvoiceDetailPage   = lazy(() => import("./pages/invoices/InvoiceDetailPage"));

// ── Payments ──────────────────────────────────────────────────────────────────
const PaymentsList        = lazy(() => import("./pages/payments/PaymentsList"));
const PaymentRecordPage   = lazy(() => import("./pages/payments/PaymentRecordPage"));
const PaymentDetailPage   = lazy(() => import("./pages/payments/PaymentDetailPage"));
const PaymentReports      = lazy(() => import("./pages/payments/PaymentReports"));

// ── Calendar ──────────────────────────────────────────────────────────────────
const CalendarDashboardPage = lazy(() => import("./pages/calendar/CalendarDashboardPage"));

// ── Drafts ────────────────────────────────────────────────────────────────────
const DraftLibraryPage    = lazy(() => import('./pages/drafts/DraftLibraryPage'));
const DraftUploadPage     = lazy(() => import('./pages/drafts/DraftUploadPage'));
// ✅ BUGFIX DRAFT-007 (Apr 9, 2026): DraftDetailPage — was missing, causing View → Dashboard redirect
const DraftDetailPage     = lazy(() => import('./pages/drafts/DraftDetailPage'));
// ✅ SPRINT-DRAFT-EDITOR-001 (Jul 2026): Phase 0 — Template Tagging page
const TemplateTaggingPage = lazy(() => import('./pages/drafts/TemplateTaggingPage'));
const DraftEditor         = lazy(() => import("./pages/DraftEditor"));

// ── Admin ─────────────────────────────────────────────────────────────────────
const AdminManageUsers    = lazy(() => import("./pages/AdminManageUsers"));
const TeamManagement      = lazy(() => import("./pages/TeamManagement"));
const AdminReports        = lazy(() => import("./pages/AdminReports"));
const AuditLogsPage       = lazy(() => import("./pages/admin/AuditLogsPage"));
// SPRINT-DPDP-DATA-RIGHTS-001 · Phase D
const ErasureRequestQueuePage = lazy(() => import("./pages/admin/ErasureRequestQueuePage"));
const FirmSettings        = lazy(() => import('./pages/FirmSettings'));
const LicenseManagement   = lazy(() => import('./pages/LicenseManagement'));

// ── Bulk Import ───────────────────────────────────────────────────────────────
const BulkImportDashboard = lazy(() => import('./pages/import/BulkImportDashboard'));
const ImportHistoryPage   = lazy(() => import('./pages/import/ImportHistoryPage'));
const ImportDetailPage    = lazy(() => import('./pages/import/ImportDetailPage'));

// ── Search / Chat ─────────────────────────────────────────────────────────────
const SearchPage          = lazy(() => import("./pages/SearchPage"));
const TeamChat            = lazy(() => import("./pages/TeamChat"));

// ── Dev / UI Tests (lazy — never in production bundle unless visited) ─────────
const TokenTest           = lazy(() => import('./components/TokenTest'));
const TailwindTest        = lazy(() => import('./components/TailwindTest'));
const UtilsTest           = lazy(() => import('./components/UtilsTest'));
const GlobalStylesTest    = lazy(() => import('./components/GlobalStylesTest'));
const TypographyTest      = lazy(() => import('./components/TypographyTest'));
const GridTest            = lazy(() => import('./components/GridTest'));
const FoundationTestingDashboard = lazy(() => import('./components/FoundationTestingDashboard'));
const DesignSystemShowcase = lazy(() => import('./components/DesignSystemShowcase'));
const IconShowcase        = lazy(() => import('./components/IconShowcase'));
const AvatarShowcase      = lazy(() => import('./components/AvatarShowcase'));
const SkeletonShowcase    = lazy(() => import('./components/SkeletonShowcase'));
const ButtonShowcase      = lazy(() => import('./components/ButtonShowcase'));
const InputShowcase       = lazy(() => import('./components/InputShowcase'));
const CheckboxShowcase    = lazy(() => import('./components/CheckboxShowcase'));
const RadioShowcase       = lazy(() => import('./components/RadioShowcase'));
const SwitchShowcase      = lazy(() => import('./components/SwitchShowcase'));
const BadgeShowcase       = lazy(() => import('./components/BadgeShowcase'));
const TagShowcase         = lazy(() => import('./components/TagShowcase'));
const CardShowcase        = lazy(() => import('./components/CardShowcase'));
const TooltipBreadcrumbShowcase = lazy(() => import('./components/TooltipBreadcrumbShowcase'));
const ComponentLibraryShowcase = lazy(() => import('./components/ComponentLibraryShowcase'));
const TablePaginationShowcase = lazy(() => import('./components/TablePaginationShowcase'));
const ComponentShowcase   = lazy(() => import('./components/ComponentShowcase'));
const PatternsShowcase    = lazy(() => import('./pages/showcases/PatternsShowcase'));
const TemplatesShowcase   = lazy(() => import('./pages/showcases/TemplatesShowcase'));
const LayoutShowcase      = lazy(() => import('./pages/showcases/LayoutShowcase'));
const NavigationShowcase  = lazy(() => import('./pages/showcases/NavigationShowcase'));


// ─────────────────────────────────────────────────────────────────────────────
// ✅ TRACK-C: OFFLINE BANNER
// ─────────────────────────────────────────────────────────────────────────────
function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOffline = () => setIsOffline(true);
    const handleOnline  = () => setIsOffline(false);

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online",  handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online",  handleOnline);
    };
  }, []);

  if (!isOffline) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-2 bg-slate-800 text-white text-sm font-medium px-4 py-2.5 shadow-lg"
    >
      <WifiOff className="w-4 h-4 flex-shrink-0" />
      <span>You're offline — changes may not save until you reconnect.</span>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// LOGIN COMPONENT
// ✅ UI-FIX (Mar 28, 2026): Restored two-column branded layout (Screenshot 1)
// ─────────────────────────────────────────────────────────────────────────────
function Login() {
  // ✅ GAP-SEC-008: Pull firmValidationError from AuthContext
  // ✅ TRACK-C: Pull licenseError — shown when license is expired/suspended
  const { login, currentUser, authStatus, loading, firmValidationError, licenseError } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState({
    email: "",
    password: "",
    rememberMe: false,
    showPassword: false,
    emailError: "",
    passwordError: "",
    isLoading: false,
    authError: "",
  });

  // ✅ BUG-001 FIX: Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetStatus, setResetStatus] = useState({ type: "", message: "" });
  const [resetLoading, setResetLoading] = useState(false);

  // ✅ PERF-001: Hide initial HTML loader once React has mounted
  useEffect(() => {
    const loader = document.getElementById('app-loader');
    if (loader) loader.style.display = 'none';
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && currentUser && authStatus === "authorized") {
      console.log("🔄 User already authenticated, redirecting...");
      navigate("/", { replace: true });
    }
  }, [currentUser, authStatus, loading, navigate]);

  const updateState = (updates) =>
    setState((prev) => ({ ...prev, ...updates }));

  const validateForm = () => {
    let isValid = true;

    if (!state.email) {
      updateState({ emailError: "Email is required" });
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) {
      updateState({ emailError: "Please enter a valid email" });
      isValid = false;
    } else {
      updateState({ emailError: "" });
    }

    if (!state.password) {
      updateState({ passwordError: "Password is required" });
      isValid = false;
    } else if (state.password.length < 6) {
      updateState({ passwordError: "Password must be at least 6 characters" });
      isValid = false;
    } else {
      updateState({ passwordError: "" });
    }

    return isValid;
  };

  const submitForm = async (e) => {
    e.preventDefault();
    updateState({ isLoading: true, emailError: "", passwordError: "", authError: "" });

    if (!validateForm()) {
      updateState({ isLoading: false });
      return;
    }

    try {
      await login(state.email, state.password);
      console.log("✅ Login completed, waiting for auth state to update...");
    } catch (error) {
      console.error("Login error:", error);
      let errorMessage = "Invalid credentials. Please try again.";

      if (error.code === "auth/user-not-found") {
        errorMessage = "No account found with this email.";
      } else if (error.code === "auth/wrong-password") {
        errorMessage = "Incorrect password. Please try again.";
      } else if (error.code === "auth/too-many-requests") {
        errorMessage = "Too many failed attempts. Please try again later.";
      } else if (error.code === "auth/invalid-credential") {
        errorMessage = "Incorrect email or password. Please try again.";
      } else if (error.code === "auth/invalid-firm-url") {
        errorMessage = "Invalid firm URL. Please navigate to your firm's login page (e.g. yourfirm.handyy.in).";
      } else if (error.code === "auth/firm-not-found") {
        errorMessage = error.message;
      }

      updateState({ isLoading: false, authError: errorMessage });
    }
  };

  // ✅ BUG-001 FIX: Forgot password handler
  const handleForgotPassword = async (e) => {
    e.preventDefault();

    const emailToReset = resetEmail || state.email;

    if (!emailToReset) {
      setResetStatus({ type: "error", message: "Please enter your email address" });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToReset)) {
      setResetStatus({ type: "error", message: "Please enter a valid email address" });
      return;
    }

    setResetLoading(true);
    setResetStatus({ type: "", message: "" });

    try {
      await sendPasswordResetEmail(auth, emailToReset, {
        url: window.location.origin + "/login",
        handleCodeInApp: false,
      });

      setResetStatus({
        type: "success",
        message: "Password reset email sent! Check your inbox (and spam folder).",
      });

      console.log("✅ Password reset email sent to:", emailToReset);

      setTimeout(() => {
        setShowForgotPassword(false);
        setResetEmail("");
        setResetStatus({ type: "", message: "" });
      }, 5000);
    } catch (error) {
      console.error("Password reset error:", error);

      let errorMessage = "Failed to send reset email. Please try again.";
      switch (error.code) {
        case "auth/user-not-found":
          errorMessage = "No account found with this email address.";
          break;
        case "auth/invalid-email":
          errorMessage = "Invalid email address format.";
          break;
        case "auth/too-many-requests":
          errorMessage = "Too many requests. Please wait a few minutes before trying again.";
          break;
        case "auth/network-request-failed":
          errorMessage = "Network error. Please check your internet connection.";
          break;
        default:
          errorMessage = error.message || errorMessage;
      }

      setResetStatus({ type: "error", message: errorMessage });
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">

      {/* ── LEFT PANEL — Branding ─────────────────────────────────────── */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 p-12 items-center justify-center relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10 max-w-md text-white">
          <div className="mb-8">
            {/* Logo — preloaded in index.html for fast LCP */}
            <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6 p-2">
              <img
                src="/lexcore-icon-white.png"
                alt="LexCore Logo"
                className="w-full h-full object-contain"
                width="48"
                height="48"
                fetchpriority="high"
              />
            </div>
            <h1 className="text-4xl font-bold mb-4">LexCore LPMS</h1>
            <p className="text-xl text-primary-100 mb-8">
              Legal Practice Management System
            </p>
            <p className="text-primary-200 text-lg leading-relaxed">
              Streamline your legal practice with comprehensive case management,
              client tracking, and financial tools.
            </p>
          </div>

          {/* Feature list */}
          <div className="space-y-4 mt-12">
            {[
              "Complete case lifecycle management",
              "Integrated billing & invoicing",
              "Client portal access",
              "Team collaboration tools",
              "Advanced reporting & analytics",
            ].map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-lexcore-mint/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-lexcore-mint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-primary-100">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL — Login Form ──────────────────────────────────── */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">

          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center mx-auto mb-3 p-2">
              <img src="/lexcore-icon-white.png" alt="LexCore" className="w-full h-full object-contain" width="32" height="32" />
            </div>
            {/* ✅ A11Y FIX: p not h1 — left panel h1 is the page title; mobile shows same content */}
            <p className="text-xl font-bold text-slate-800">LexCore LPMS</p>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Welcome back</h2>
            <p className="text-slate-500">Sign in to your account to continue</p>
          </div>

          {/* ✅ TRACK-C: License error banner */}
          {licenseError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{licenseError}</p>
            </div>
          )}

          {/* ✅ GAP-SEC-008: Firm validation error */}
          {firmValidationError && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-700">{firmValidationError}</p>
            </div>
          )}

          {/* Auth Error */}
          {state.authError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{state.authError}</p>
            </div>
          )}

          <form onSubmit={submitForm} className="space-y-4" noValidate>
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={state.email}
                onChange={(e) => updateState({ email: e.target.value, emailError: "" })}
                className={`w-full px-3.5 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  state.emailError
                    ? "border-red-300 bg-red-50"
                    : "border-slate-300 bg-white hover:border-slate-400"
                }`}
                placeholder="you@firm.com"
                aria-describedby={state.emailError ? "email-error" : undefined}
              />
              {state.emailError && (
                <p id="email-error" className="mt-1.5 text-xs text-red-600">{state.emailError}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={state.showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={state.password}
                  onChange={(e) => updateState({ password: e.target.value, passwordError: "" })}
                  className={`w-full px-3.5 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 pr-10 ${
                    state.passwordError
                      ? "border-red-300 bg-red-50"
                      : "border-slate-300 bg-white hover:border-slate-400"
                  }`}
                  placeholder="••••••••"
                  aria-describedby={state.passwordError ? "password-error" : undefined}
                />
                <button
                  type="button"
                  onClick={() => updateState({ showPassword: !state.showPassword })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label={state.showPassword ? "Hide password" : "Show password"}
                >
                  {state.showPassword ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {state.passwordError && (
                <p id="password-error" className="mt-1.5 text-xs text-red-600">{state.passwordError}</p>
              )}
            </div>

            {/* Remember me + Forgot password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={state.rememberMe}
                  onChange={(e) => updateState({ rememberMe: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-slate-600">Remember me</span>
              </label>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Forgot password?
              </button>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={state.isLoading}
              className="w-full py-2.5 px-4 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium text-sm rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center justify-center gap-2"
            >
              {state.isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-slate-400">
            © {new Date().getFullYear()} LexCore LawTech LLP. All rights reserved.
          </p>
        </div>
      </div>

      {/* ✅ BUG-001 FIX: Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-1">Reset your password</h3>
            <p className="text-sm text-slate-500 mb-4">
              Enter your email and we'll send you a reset link.
            </p>

            {resetStatus.message && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${
                resetStatus.type === "success"
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}>
                {resetStatus.message}
              </div>
            )}

            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Email address
                </label>
                <input
                  id="reset-email"
                  type="email"
                  value={resetEmail || state.email}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="you@firm.com"
                />
              </div>

              <button
                type="submit"
                disabled={resetLoading}
                className="w-full py-2.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white font-medium text-sm rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {resetLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </>
                ) : "Send reset link"}
              </button>
            </form>

            <p className="mt-4 text-center text-sm">
              <button
                type="button"
                onClick={() => setShowForgotPassword(false)}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Back to login
              </button>
            </p>
          </div>
        </div>
      )}

    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// ✅ TRACK-A: UPGRADE PROMPT
// ─────────────────────────────────────────────────────────────────────────────
function UpgradePrompt({ featureName = "this feature" }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
      <div className="w-16 h-16 rounded-full bg-amber-50 border-2 border-amber-200 flex items-center justify-center mb-4">
        <span className="text-2xl">🔒</span>
      </div>
      <h2 className="text-lg font-semibold text-slate-800 mb-2">
        Feature not available on your plan
      </h2>
      <p className="text-sm text-slate-500 max-w-sm mb-4">
        <strong>{featureName}</strong> is not included in your firm's current
        license. Contact{" "}
        <a href="mailto:support@lexcorelawtech.in" className="text-blue-600 hover:underline">
          support@lexcorelawtech.in
        </a>{" "}
        to upgrade.
      </p>
      <button
        onClick={() => window.history.back()}
        className="text-sm text-slate-500 hover:text-slate-700 underline"
      >
        ← Go back
      </button>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// ✅ TRACK-A: FEATURE ROUTE GUARD
// ✅ PERF-002: licenseService loaded dynamically — not part of Login bundle
// ─────────────────────────────────────────────────────────────────────────────
function FeatureRoute({ children, feature, featureName, firmId }) {
  const [allowed, setAllowed] = useState(null);

  useEffect(() => {
    if (!firmId || !feature) {
      setAllowed(true);
      return;
    }

    let cancelled = false;

    // ✅ PERF-002: Dynamic import — LicenseService (252 lines + Firestore) only
    // loads when a FeatureRoute is actually rendered (post-auth, never on Login)
    import('./services/LicenseService').then(({ default: licenseService }) => {
      return licenseService.getLicense(firmId).then((license) => {
        if (!cancelled) {
          setAllowed(licenseService.hasFeature(license, feature));
        }
      });
    }).catch((err) => {
      console.error("FeatureRoute license check error:", err);
      if (!cancelled) setAllowed(true); // Fail open
    });

    return () => { cancelled = true; };
  }, [firmId, feature]);

  if (allowed === null) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!allowed) return <UpgradePrompt featureName={featureName} />;
  return children;
}


// ─────────────────────────────────────────────────────────────────────────────
// ROUTE GUARD COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function RequireAuth({ children, allowedRoles = [] }) {
  const { currentUser, userRole, loading, authStatus } = useAuth();

  if (loading || authStatus === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!currentUser || authStatus !== "authorized") {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
}


// ─────────────────────────────────────────────────────────────────────────────
// INITIAL ROUTE — redirects based on role
// ─────────────────────────────────────────────────────────────────────────────
function InitialRoute() {
  const { currentUser, userRole, loading, authStatus } = useAuth();
  const navigate = useNavigate();

  // ✅ PERF-001: Hide initial HTML loader once React is running
  useEffect(() => {
    const loader = document.getElementById('app-loader');
    if (loader) loader.style.display = 'none';
  }, []);

  useEffect(() => {
    if (loading || authStatus === "checking") return;
    if (!currentUser || authStatus !== "authorized") {
      navigate("/login", { replace: true });
      return;
    }

    let route = "/admin/dashboard";
    switch (userRole) {
      case "Admin":
      case "ManagingPartner":
      case "SrAssociate":
        route = "/admin/dashboard"; break;
      case "Partner":
        route = "/partner/dashboard"; break;
      case "Associate":
        route = "/associate/dashboard"; break;
      case "Intern":
        route = "/intern/dashboard"; break;
      case "Receptionist":
        route = "/receptionist/dashboard"; break;
      case "Accountant":
        route = "/accounts"; break;
      default:
        route = "/unauthorized";
    }
    navigate(route, { replace: true });
  }, [currentUser, userRole, loading, authStatus, navigate]);

  return <div>Redirecting...</div>;
}


// ─────────────────────────────────────────────────────────────────────────────
// MAIN APP ROUTES
// ✅ GAP-SEC-008: firmId from useAuth() — no hardcoded "firm1" anywhere.
// ✅ TRACK-C: HelpDrawer + HelpButton + OfflineBanner wired here.
// ✅ PERF-001: All routes wrapped in Suspense for lazy loading
// ─────────────────────────────────────────────────────────────────────────────
function AppRoutes() {
  const { loading, authStatus, firmId, userRole, currentUser } = useAuth();

  if (loading || authStatus === "checking") return <PageLoader />;

  return (
    <>
      {/* ✅ TRACK-C: Offline banner — always shown */}
      <OfflineBanner />

      {/* ✅ PERF-001: Suspense wraps ALL lazy-loaded routes */}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/client-login" element={<ClientLogin />} />

          {/* Test & Showcase Routes */}
          <Route path="/token-test" element={<TokenTest />} />
          <Route path="/tailwind-test" element={<TailwindTest />} />
          <Route path="/utils-test" element={<UtilsTest />} />
          <Route path="/global-styles-test" element={<GlobalStylesTest />} />
          <Route path="/typography-test" element={<TypographyTest />} />
          <Route path="/grid-test" element={<GridTest />} />
          <Route path="/foundation-testing" element={<FoundationTestingDashboard />} />
          <Route path="/design-system" element={<DesignSystemShowcase />} />
          <Route path="/icon-system" element={<IconShowcase />} />
          <Route path="/avatar-system" element={<AvatarShowcase />} />
          <Route path="/skeleton-loader" element={<SkeletonShowcase />} />
          <Route path="/showcase/buttons" element={<ButtonShowcase />} />
          <Route path="/showcase/inputs" element={<InputShowcase />} />
          <Route path="/showcase/checkbox" element={<CheckboxShowcase />} />
          <Route path="/showcase/radio" element={<RadioShowcase />} />
          <Route path="/showcase/switch" element={<SwitchShowcase />} />
          <Route path="/showcase/badge" element={<BadgeShowcase />} />
          <Route path="/showcase/tag" element={<TagShowcase />} />
          <Route path="/showcase/card" element={<CardShowcase />} />
          <Route path="/showcase/ttb" element={<TooltipBreadcrumbShowcase />} />
          <Route path="/showcase/cls" element={<ComponentLibraryShowcase />} />
          <Route path="/showcase/tps" element={<TablePaginationShowcase />} />
          <Route path="/showcase/csc" element={<ComponentShowcase />} />
          <Route path="/showcases/patterns" element={<PatternsShowcase />} />
          <Route path="/showcases/templates" element={<TemplatesShowcase />} />
          <Route path="/showcases/layout" element={<LayoutShowcase />} />
          <Route path="/showcases/navigation" element={<NavigationShowcase />} />

          <Route path="/" element={<InitialRoute />} />
          <Route path="/client-portal" element={<ClientPortalDashboard />} />
          <Route path="/cases/:id/diagnostic" element={<CaseDetailDiagnostic />} />

          {/* Role Layouts */}
          <Route path="/partner" element={<RequireAuth allowedRoles={["Partner"]}><PartnerLayout /></RequireAuth>}>
            <Route path="dashboard" element={<PartnerDashboard />} />
            <Route path="cases" element={<CasesList />} />
            <Route path="clients" element={<ClientList />} />
            <Route path="calendar" element={<CalendarDashboardPage />} />
          </Route>

          <Route path="/associate" element={<RequireAuth allowedRoles={["Associate"]}><AssociateLayout /></RequireAuth>}>
            <Route path="dashboard" element={<AssociateDashboard />} />
            <Route path="cases" element={<CasesList />} />
            <Route path="clients" element={<ClientList />} />
            <Route path="calendar" element={<CalendarDashboardPage />} />
          </Route>

          <Route path="/intern" element={<RequireAuth allowedRoles={["Intern"]}><InternLayout /></RequireAuth>}>
            <Route path="dashboard" element={<InternDashboard />} />
            <Route path="cases" element={<CasesList />} />
            <Route path="calendar" element={<CalendarDashboardPage />} />
          </Route>

          <Route path="/accountant" element={<RequireAuth allowedRoles={["Accountant"]}><AccountantLayout /></RequireAuth>}>
            <Route path="" element={<Navigate to="/accounts" replace />} />
            <Route path="clients" element={<ClientList />} />
            <Route path="clients/:id" element={<ClientView />} />
          </Route>

          <Route
            path="/accounts"
            element={
              <RequireAuth allowedRoles={["Admin", "ManagingPartner", "Accountant"]}>
                <ErrorBoundary context="AccountsDashboard">
                  <AccountsDashboard />
                </ErrorBoundary>
              </RequireAuth>
            }
          />

          <Route path="/receptionist" element={<RequireAuth allowedRoles={["Receptionist"]}><ReceptionistLayout /></RequireAuth>}>
            <Route path="dashboard" element={<ReceptionistDashboard />} />
            <Route path="clients" element={<ClientList />} />
            <Route path="clients/create" element={<ClientCreate />} />
            <Route path="clients/:id" element={<ClientView />} />
            <Route path="calendar" element={<CalendarDashboardPage />} />
            {/* ✅ BUG-RECEP-VIEW-001 FIX (Apr 11, 2026): Cases, Invoices, Payments — view only. */}
            <Route path="cases" element={<CasesList />} />
            <Route path="cases/:id" element={<CaseDetail />} />
            <Route path="invoices" element={<InvoicesListPage />} />
            <Route path="invoices/:id" element={<InvoiceDetailPage />} />
            <Route path="payments" element={<PaymentsList />} />
            <Route path="payments/:id" element={<PaymentDetailPage />} />
          </Route>

          {/* ── Admin Routes ────────────────────────────────────────────────── */}
          <Route
            path="/admin"
            element={
              // ✅ HIERARCHY-FIX (Apr 9, 2026): Partner added. Partner=Level3 > SrAssociate=Level4.
              <RequireAuth allowedRoles={["Admin", "ManagingPartner", "Partner", "SrAssociate"]}>
                <AdminLayout />
              </RequireAuth>
            }
          >
            <Route path="dashboard" element={
              userRole === 'SrAssociate'
                ? <ErrorBoundary context="SrAssociateDashboard"><SrAssociateDashboard /></ErrorBoundary>
                : <ErrorBoundary context="AdminDashboard"><AdminDashboard /></ErrorBoundary>
            } />
            <Route path="cases" element={<CasesList />} />
            <Route path="manage-users" element={<AdminManageUsers />} />
            <Route path="team-management" element={<TeamManagement />} />

            <Route path="reports" element={<AdminReports firmId={firmId} />} />
            <Route path="audit-logs" element={<AuditLogsPage firmId={firmId} />} />

            {/* SPRINT-DPDP-DATA-RIGHTS-001 · Phase D — ADR-DPDP-DATA-RIGHTS-001 §8a.
                Stricter RequireAuth nested here deliberately: the outer /admin
                wrapper above also admits Partner and SrAssociate for other
                pages, but can_approve_data_erasure / can_handle_dpdp_grievances
                default to Admin + ManagingPartner ONLY. Nav visibility
                (navigation.js's adminNavigation, shared by Admin/ManagingPartner
                only) is a convenience — this nested RequireAuth is the actual
                enforcement, matching D-T2 in the Phase D test checklist. */}
            <Route
              path="dpdp-erasure-queue"
              element={
                <RequireAuth allowedRoles={["Admin", "ManagingPartner"]}>
                  <ErasureRequestQueuePage />
                </RequireAuth>
              }
            />

            <Route path="clients" element={<ClientList />} />
            <Route path="clients/create" element={<ClientCreate />} />
            <Route path="clients/:id" element={<ClientView />} />
            <Route path="invoices" element={<InvoicesListPage />} />
            <Route path="invoices/create" element={<InvoiceCreatePage />} />
            <Route path="invoices/:id" element={<InvoiceDetailPage />} />
            <Route path="payments" element={<PaymentsList />} />
            <Route path="payments/record" element={<PaymentRecordPage />} />
            <Route path="payments/:id" element={<PaymentDetailPage />} />
            <Route path="payments/reports" element={<PaymentReports />} />
            <Route path="calendar" element={<CalendarDashboardPage />} />

            {/* ✅ TRACK-A: Firm Settings */}
            <Route
              path="settings"
              element={
                <RequireAuth allowedRoles={["Admin", "ManagingPartner"]}>
                  <FirmSettings />
                </RequireAuth>
              }
            />

            {/* ✅ TRACK-C: License & Plan — Admin + ManagingPartner only */}
            <Route
              path="license"
              element={
                <RequireAuth allowedRoles={["Admin", "ManagingPartner"]}>
                  <LicenseManagement />
                </RequireAuth>
              }
            />

            {/* ✅ TRACK-A: Bulk Import gated behind BULK_IMPORT feature flag */}
            <Route path="imports" element={
              <FeatureRoute feature={FEATURE_FLAGS.BULK_IMPORT} featureName="Bulk Import & eCourts" firmId={firmId}>
                <BulkImportDashboard />
              </FeatureRoute>
            } />
            <Route path="imports/history" element={
              <FeatureRoute feature={FEATURE_FLAGS.BULK_IMPORT} featureName="Bulk Import & eCourts" firmId={firmId}>
                <ImportHistoryPage />
              </FeatureRoute>
            } />
            <Route path="imports/:jobId" element={
              <FeatureRoute feature={FEATURE_FLAGS.BULK_IMPORT} featureName="Bulk Import & eCourts" firmId={firmId}>
                <ImportDetailPage />
              </FeatureRoute>
            } />
          </Route>

          {/* General Routes */}
          <Route
            path="/cases"
            element={
              <RequireAuth allowedRoles={["Admin", "ManagingPartner", "SrAssociate", "Partner", "Associate", "Intern", "Receptionist"]}>
                <DashboardLayout>
                  <CasesList />
                </DashboardLayout>
              </RequireAuth>
            }
          />

          <Route
            path="/case-create"
            element={
              <RequireAuth allowedRoles={["Admin", "ManagingPartner", "SrAssociate", "Partner"]}>
                <DashboardLayout noPadding={true}>
                  <CaseCreate />
                </DashboardLayout>
              </RequireAuth>
            }
          />

          {/* ✅ BUG-ROUTE-001 FIX (Apr 16, 2026): /cases/create was matching /cases/:id with id="create".
              Redirect /cases/create → /case-create so the create form loads correctly. */}
          <Route
            path="/cases/create"
            element={<Navigate to="/case-create" replace />}
          />

          <Route
            path="/cases/:id"
            element={
              <RequireAuth allowedRoles={["Admin", "ManagingPartner", "SrAssociate", "Partner", "Associate", "Intern", "Receptionist"]}>
                <ErrorBoundary context="CaseDetail">
                  <DashboardLayout>
                    <CaseDetail />
                  </DashboardLayout>
                </ErrorBoundary>
              </RequireAuth>
            }
          />

          <Route
            path="/cases/:id/edit"
            element={
              <RequireAuth allowedRoles={["Admin", "ManagingPartner", "Partner", "SrAssociate"]}>
                <DashboardLayout>
                  <CaseEdit />
                </DashboardLayout>
              </RequireAuth>
            }
          />

          <Route
            path="/calendar"
            element={
              <RequireAuth allowedRoles={["Admin", "ManagingPartner", "Partner", "SrAssociate", "Associate", "Receptionist"]}>
                <DashboardLayout>
                  <CalendarDashboardPage />
                </DashboardLayout>
              </RequireAuth>
            }
          />

          {/* ✅ TRACK-A: Draft Library gated */}
          <Route
            path="/drafts"
            element={
              <RequireAuth allowedRoles={["Admin", "ManagingPartner", "SrAssociate", "Associate", "Partner", "Intern", "Accountant"]}>
                <DashboardLayout>
                  <FeatureRoute feature={FEATURE_FLAGS.DRAFTS} featureName="Draft Library" firmId={firmId}>
                    <DraftLibraryPage />
                  </FeatureRoute>
                </DashboardLayout>
              </RequireAuth>
            }
          />

          <Route
            path="/drafts/upload"
            element={
              // ✅ BUGFIX DRAFT-002 (Apr 9, 2026): Added "Partner" to allowedRoles.
              <RequireAuth allowedRoles={["Admin", "ManagingPartner", "Partner", "SrAssociate"]}>
                <DashboardLayout>
                  <FeatureRoute feature={FEATURE_FLAGS.DRAFTS} featureName="Draft Library" firmId={firmId}>
                    <DraftUploadPage />
                  </FeatureRoute>
                </DashboardLayout>
              </RequireAuth>
            }
          />

          {/* ✅ SPRINT-DRAFT-EDITOR-001 (Jul 2026): Phase 0 — Template Tagging.
              MUST come before /drafts/:id (route specificity) — "tagging"
              would otherwise be captured as an :id param. */}
          <Route
            path="/drafts/tagging"
            element={
              <RequireAuth allowedRoles={["Admin", "ManagingPartner", "Partner", "SrAssociate", "Associate"]}>
                <DashboardLayout>
                  <FeatureRoute feature={FEATURE_FLAGS.DRAFTS} featureName="Draft Library" firmId={firmId}>
                    <TemplateTaggingPage />
                  </FeatureRoute>
                </DashboardLayout>
              </RequireAuth>
            }
          />

          {/* ✅ BUGFIX DRAFT-007 (Apr 9, 2026): /drafts/:id was MISSING. */}
          <Route
            path="/drafts/:id"
            element={
              <RequireAuth allowedRoles={["Admin", "ManagingPartner", "SrAssociate", "Associate", "Partner", "Intern", "Accountant"]}>
                <DashboardLayout>
                  <FeatureRoute feature={FEATURE_FLAGS.DRAFTS} featureName="Draft Library" firmId={firmId}>
                    <DraftDetailPage />
                  </FeatureRoute>
                </DashboardLayout>
              </RequireAuth>
            }
          />

          {/* Invoice Management */}
          <Route path="/invoices" element={
            <RequireAuth allowedRoles={["Admin", "ManagingPartner", "Partner", "SrAssociate", "Associate", "Intern", "Receptionist", "Accountant"]}>
              <DashboardLayout><InvoicesListPage /></DashboardLayout>
            </RequireAuth>
          } />
          <Route path="/invoices/create" element={
            <RequireAuth allowedRoles={["Admin", "ManagingPartner", "Partner", "Accountant"]}>
              <DashboardLayout><InvoiceCreatePage /></DashboardLayout>
            </RequireAuth>
          } />
          <Route path="/invoices/:id" element={
            <RequireAuth allowedRoles={["Admin", "ManagingPartner", "Partner", "SrAssociate", "Associate", "Intern", "Receptionist", "Accountant"]}>
              <DashboardLayout><InvoiceDetailPage /></DashboardLayout>
            </RequireAuth>
          } />

          {/* Payments */}
          <Route path="/payments" element={
            <RequireAuth allowedRoles={["Admin", "ManagingPartner", "Accountant"]}>
              <DashboardLayout><PaymentsList /></DashboardLayout>
            </RequireAuth>
          } />
          <Route path="/payments/record" element={
            <RequireAuth allowedRoles={["Admin", "ManagingPartner", "Accountant"]}>
              <DashboardLayout><PaymentRecordPage /></DashboardLayout>
            </RequireAuth>
          } />
          <Route path="/payments/:id" element={
            <RequireAuth allowedRoles={["Admin", "ManagingPartner", "Accountant"]}>
              <DashboardLayout><PaymentDetailPage /></DashboardLayout>
            </RequireAuth>
          } />
          <Route path="/payments/reports" element={
            <RequireAuth allowedRoles={["Admin", "ManagingPartner", "Accountant"]}>
              <DashboardLayout><PaymentReports /></DashboardLayout>
            </RequireAuth>
          } />

          {/* Clients */}
          <Route path="/clients" element={
            <RequireAuth allowedRoles={["Admin", "ManagingPartner", "SrAssociate", "Partner", "Associate", "Intern", "Receptionist", "Accountant"]}>
              <DashboardLayout><ClientList /></DashboardLayout>
            </RequireAuth>
          } />
          <Route path="/clients/create" element={
            // ✅ HIERARCHY-FIX (Apr 9, 2026): Partner added.
            <RequireAuth allowedRoles={["Admin", "ManagingPartner", "Partner", "SrAssociate", "Receptionist"]}>
              <DashboardLayout><ClientCreate /></DashboardLayout>
            </RequireAuth>
          } />
          <Route path="/clients/:id" element={
            <RequireAuth allowedRoles={["Admin", "ManagingPartner", "SrAssociate", "Partner", "Associate", "Intern", "Receptionist", "Accountant"]}>
              <ErrorBoundary context="ClientView">
                <DashboardLayout><ClientView /></DashboardLayout>
              </ErrorBoundary>
            </RequireAuth>
          } />

          <Route path="/kpi" element={
            <RequireAuth allowedRoles={["Admin", "ManagingPartner"]}>
              <KPIDashboard />
            </RequireAuth>
          } />

          <Route path="/team-chat" element={
            <RequireAuth>
              <TeamChat firmId={firmId} />
            </RequireAuth>
          } />

          <Route path="/search" element={<RequireAuth><SearchPage /></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// ROOT APP
// ─────────────────────────────────────────────────────────────────────────────
function App() {
  // ✅ FIX KAL-005 FINAL (Jun 24, 2026): Render ClientPortalDashboard OUTSIDE AuthProvider.
  // AuthProvider's onAuthStateChanged fires, sees no Firebase user, and causes repeated
  // re-renders even with the bypass fix — unmounting ClientPortalDashboard mid-fetch,
  // preventing setLoading(false) from ever completing.
  // Fix: detect /client-portal path at the App level and render the portal component
  // completely outside AuthProvider so zero auth state changes can affect it.
  // ClientPortalDashboard validates security independently via JWT → getClientData CF.
  if (window.location.pathname === '/client-portal') {
    // ✅ FIX KAL-005 ROOT CAUSE (Jun 25, 2026): Hide app-loader immediately.
    // The static HTML app-loader spinner in index.html is normally hidden by a
    // useEffect inside the auth flow — which the portal bypass skips entirely.
    // Without this line, app-loader sits on top of the rendered portal forever.
    const loader = document.getElementById('app-loader');
    if (loader) loader.style.display = 'none';

    return (
      <RootErrorBoundary>
        <BrowserRouter>
          <ClientPortalDashboardDirect />
        </BrowserRouter>
      </RootErrorBoundary>
    );
  }
  return (
    <RootErrorBoundary>
      <AuthProvider>
        <UIProvider>
          <ToastProvider>
            <HelpProvider>
              <BrowserRouter>
                {/* ✅ BUG-002 FIX: Session warning modal */}
                <SessionWarningModal />
                <AppRoutes />
              </BrowserRouter>
            </HelpProvider>
          </ToastProvider>
        </UIProvider>
      </AuthProvider>
    </RootErrorBoundary>
  );
}

export default App;
